import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, Send, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function AdminInbox({ onUnreadChange }) {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/admin/threads").then(({ data }) => {
      setThreads(data || []);
      const totalUnread = (data || []).reduce((a, t) => a + (t.unread || 0), 0);
      onUnreadChange?.(totalUnread);
    }).catch(() => setThreads(false));
  }, [onUnreadChange]);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 10000);
    return () => clearInterval(t);
  }, [loadThreads]);

  const openThread = async (uid) => {
    setActive(uid);
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/admin/threads/${uid}`);
      setMsgs(data || []);
      loadThreads();
    } catch (e) { toast.error("Could not load thread"); }
    finally { setLoadingThread(false); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const reply = async () => {
    if (!text.trim() || !active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/threads/${active}`, { body: text.trim() });
      setMsgs((m) => [...m, data]);
      setText("");
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reply failed"); }
    finally { setBusy(false); }
  };

  if (threads === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (threads === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load messages.</p>;

  const activeThread = threads.find((t) => t.user_id === active);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-testid="admin-inbox" style={{ minHeight: 480 }}>
      <div className="rounded-2xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid="inbox-threads">
        <div className="px-4 py-3 border-b font-display font-bold flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
          <Inbox className="w-4 h-4 text-primary-c" /> Conversations
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {threads.length === 0 ? (
            <p className="text-sm text-center py-10" style={{ color: "var(--text-2)" }}>No messages from users yet.</p>
          ) : threads.map((t) => (
            <button key={t.user_id} data-testid={`inbox-thread-${t.user_id}`} onClick={() => openThread(t.user_id)}
              className="w-full text-left px-4 py-3 border-b last:border-0 hover:bg-[var(--bg)] transition-colors"
              style={{ borderColor: "var(--border)", background: active === t.user_id ? "var(--bg)" : "transparent" }}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm truncate">{t.name || t.email}</span>
                {t.unread > 0 && <span data-testid={`inbox-unread-${t.user_id}`} className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>{t.unread}</span>}
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs truncate" style={{ color: "var(--text-2)" }}>{t.last_sender === "admin" ? "You: " : ""}{t.last_body}</span>
                <span className="text-[10px] mono shrink-0" style={{ color: "var(--text-2)" }}>{timeAgo(t.last_at)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl glow-border flex flex-col" style={{ background: "var(--surface)" }} data-testid="inbox-conversation">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8" style={{ color: "var(--text-2)" }}>
            <MessageSquare className="w-8 h-8 opacity-60" />
            <p className="mt-3 text-sm">Select a conversation to read and reply.</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="font-bold text-sm">{activeThread?.name || activeThread?.email}</div>
              <div className="text-xs mono" style={{ color: "var(--text-2)" }}>{activeThread?.email}</div>
            </div>
            <div className="flex-1 p-4 space-y-2 overflow-y-auto max-h-[420px]" data-testid="inbox-messages" style={{ background: "var(--bg)" }}>
              {loadingThread ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>
              ) : msgs.map((m) => (
                <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "admin" ? "btn-glow" : "glass"}`}
                    style={m.sender === "admin" ? {} : { color: "var(--text)" }} data-testid={`inbox-msg-${m.sender}`}>
                    <span className="whitespace-pre-wrap">{m.body}</span>
                    <div className="text-[9px] mono mt-1 opacity-70">{timeAgo(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--border)" }}>
              <input data-testid="inbox-reply-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), reply())}
                placeholder="Type a reply…"
                className="flex-1 rounded-full bg-surface glow-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
              <button data-testid="inbox-reply-send" onClick={reply} disabled={busy || !text.trim()}
                className="btn-glow rounded-full px-4 flex items-center gap-2 font-bold disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
